import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  InputLabel,
  FormControl,
  FormHelperText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import {
  SaveOutlined,
  WarningAmberOutlined,
  Close,
  Add,
  Search,
  RestartAlt,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';

interface SettingItem {
  key: string;
  value: string;
  description: string;
  category: string;
  updatedAt: string;
}

type SettingsMap = Record<string, SettingItem[]>;

const CATEGORY_KEYS = ['general', 'security', 'email', 'features', 'registration'] as const;
const TAB_LABELS = ['General', 'Security', 'Email', 'Feature Flags', 'Registration'];

const DEFAULT_BLOCKED_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'gmx.net', 'live.com', 'msn.com', 'me.com',
  'inbox.com', 'fastmail.com', 'hushmail.com', 'tutanota.com', 'mailfence.com',
  'guerrillamail.com', 'tempmail.com', 'throwaway.email', 'mailinator.com',
  'sharklasers.com', 'guerrillamailblock.com', 'grr.la', 'dispostable.com',
];

type SettingValue = string | boolean | number;

function parseValue(value: string): SettingValue {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
}

function stringifyValue(value: SettingValue): string {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

const AdminSettings: React.FC = () => {
  const { showNotification } = useNotification();
  const [settings, setSettings] = useState<SettingsMap>({});
  const [formValues, setFormValues] = useState<Record<string, SettingValue>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, SettingValue>>({});
  const [tabIndex, setTabIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Registration tab state
  const [blockedDomains, setBlockedDomains] = useState<string[]>([]);
  const [originalDomains, setOriginalDomains] = useState<string[]>([]);
  const [domainsDirty, setDomainsDirty] = useState(false);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainsSaving, setDomainsSaving] = useState(false);
  const [domainsLoaded, setDomainsLoaded] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newDomainError, setNewDomainError] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState<{ allowed: boolean; domain?: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [domainFilter, setDomainFilter] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminService.getSettings();
      const data: SettingsMap = response.data;
      setSettings(data);

      const values: Record<string, SettingValue> = {};
      for (const category of Object.keys(data)) {
        for (const setting of data[category]) {
          values[setting.key] = parseValue(setting.value);
        }
      }
      setFormValues(values);
      setOriginalValues({ ...values });
    } catch {
      showNotification('Failed to load settings', 'error');
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedDomains = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const res = await adminService.getBlockedDomains();
      const domains: string[] = res.data.domains || [];
      setBlockedDomains([...domains].sort());
      setOriginalDomains([...domains].sort());
      setDomainsDirty(false);
      setDomainsLoaded(true);
    } catch {
      showNotification('Failed to load blocked domains', 'error');
    } finally {
      setDomainsLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    if (tabIndex === 4 && !domainsLoaded) {
      fetchBlockedDomains();
    }
  }, [tabIndex, domainsLoaded, fetchBlockedDomains]);

  const currentCategory = CATEGORY_KEYS[tabIndex];

  const isDirty = (): boolean => {
    const categorySettings = settings[currentCategory] || [];
    return categorySettings.some(
      (s) => stringifyValue(formValues[s.key]) !== stringifyValue(originalValues[s.key]),
    );
  };

  const getLastUpdated = (): string | null => {
    const categorySettings = settings[currentCategory] || [];
    if (categorySettings.length === 0) return null;
    const sorted = [...categorySettings].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    return sorted[0].updatedAt;
  };

  const handleFieldChange = (key: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const categorySettings = settings[currentCategory] || [];
      const changed = categorySettings
        .filter(
          (s) => stringifyValue(formValues[s.key]) !== stringifyValue(originalValues[s.key]),
        )
        .map((s) => ({ key: s.key, value: stringifyValue(formValues[s.key]) }));

      if (changed.length === 0) return;

      await adminService.updateSettings(changed);
      setOriginalValues((prev) => {
        const updated = { ...prev };
        for (const c of changed) {
          updated[c.key] = parseValue(c.value);
        }
        return updated;
      });
      setSaveSuccess(true);

      // Refresh to get updated timestamps
      const response = await adminService.getSettings();
      setSettings(response.data);
    } catch {
      showNotification('Failed to save settings', 'error');
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const lastUpdated = getLastUpdated();
  const dirty = isDirty();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && Object.keys(settings).length === 0) {
    return (
      <Alert severity="error" sx={{ borderRadius: '12px' }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" fontWeight={600}>
          Platform Settings
        </Typography>
        {lastUpdated && (
          <Typography variant="body2" color="text.secondary">
            Last updated: {dayjs(lastUpdated).format('MMM D, YYYY h:mm A')}
          </Typography>
        )}
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 2,
          }}
        >
          {TAB_LABELS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabIndex === 0 && renderGeneralTab()}
          {tabIndex === 1 && renderSecurityTab()}
          {tabIndex === 2 && renderEmailTab()}
          {tabIndex === 3 && renderFeatureFlagsTab()}
          {tabIndex === 4 && renderRegistrationTab()}

          {tabIndex !== 4 && (
            <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />}
                disabled={saving || !dirty}
                onClick={handleSave}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              {dirty && (
                <Chip
                  icon={<WarningAmberOutlined />}
                  label="Unsaved changes"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={4000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSaveSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Settings saved successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error && Object.keys(settings).length > 0}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );

  function renderGeneralTab() {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          label="Platform Name"
          fullWidth
          value={formValues['platform.name'] ?? ''}
          onChange={(e) => handleFieldChange('platform.name', e.target.value)}
        />
        <TextField
          label="Logo URL"
          fullWidth
          value={formValues['platform.logo_url'] ?? ''}
          onChange={(e) => handleFieldChange('platform.logo_url', e.target.value)}
          helperText="Enter URL for custom logo"
        />
        <TextField
          label="Default Timezone"
          fullWidth
          value={formValues['platform.default_timezone'] ?? ''}
          onChange={(e) => handleFieldChange('platform.default_timezone', e.target.value)}
        />
        <TextField
          label="Default Max Students"
          fullWidth
          type="number"
          value={formValues['platform.default_max_students'] ?? ''}
          onChange={(e) => handleFieldChange('platform.default_max_students', e.target.value)}
        />
        <TextField
          label="Default Session Duration"
          fullWidth
          type="number"
          value={formValues['platform.default_session_duration'] ?? ''}
          onChange={(e) => handleFieldChange('platform.default_session_duration', e.target.value)}
          helperText="minutes"
        />
        <TextField
          label="Max Session Duration"
          fullWidth
          type="number"
          value={formValues['platform.max_session_duration'] ?? ''}
          onChange={(e) => handleFieldChange('platform.max_session_duration', e.target.value)}
          helperText="minutes"
        />
      </Box>
    );
  }

  function renderSecurityTab() {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          label="Minimum Password Length"
          fullWidth
          type="number"
          inputProps={{ min: 6, max: 32 }}
          value={formValues['security.password_min_length'] ?? ''}
          onChange={(e) => handleFieldChange('security.password_min_length', e.target.value)}
        />
        <FormControlLabel
          control={
            <Switch
              checked={formValues['security.password_complexity'] === true}
              onChange={(e) => handleFieldChange('security.password_complexity', e.target.checked)}
            />
          }
          label="Require Password Complexity"
        />
        <TextField
          label="Max Login Attempts Before Lockout"
          fullWidth
          type="number"
          value={formValues['security.max_login_attempts'] ?? ''}
          onChange={(e) => handleFieldChange('security.max_login_attempts', e.target.value)}
        />
        <TextField
          label="Lockout Duration"
          fullWidth
          type="number"
          value={formValues['security.lockout_duration'] ?? ''}
          onChange={(e) => handleFieldChange('security.lockout_duration', e.target.value)}
          helperText="minutes"
        />
        <TextField
          label="Session Timeout"
          fullWidth
          type="number"
          value={formValues['security.session_timeout'] ?? ''}
          onChange={(e) => handleFieldChange('security.session_timeout', e.target.value)}
          helperText="hours"
        />
      </Box>
    );
  }

  function renderEmailTab() {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Alert severity="info" sx={{ borderRadius: '8px' }}>
          Email service integration coming soon. Configure settings now for when it&apos;s available.
        </Alert>
        <FormControlLabel
          control={
            <Switch
              checked={formValues['email.enabled'] === true}
              onChange={(e) => handleFieldChange('email.enabled', e.target.checked)}
            />
          }
          label="Email Enabled"
        />
        <FormControl fullWidth>
          <InputLabel id="email-provider-label">Provider</InputLabel>
          <Select
            labelId="email-provider-label"
            label="Provider"
            value={formValues['email.provider'] ?? ''}
            onChange={(e) => handleFieldChange('email.provider', e.target.value)}
          >
            <MenuItem value="aws_ses">AWS SES</MenuItem>
            <MenuItem value="smtp">SMTP</MenuItem>
          </Select>
          <FormHelperText>Select the email delivery provider</FormHelperText>
        </FormControl>
        <TextField
          label="From Address"
          fullWidth
          value={formValues['email.from_address'] ?? ''}
          onChange={(e) => handleFieldChange('email.from_address', e.target.value)}
        />
        <TextField
          label="From Name"
          fullWidth
          value={formValues['email.from_name'] ?? ''}
          onChange={(e) => handleFieldChange('email.from_name', e.target.value)}
        />
        <Tooltip title="Not yet available" arrow>
          <span>
            <Button variant="outlined" disabled>
              Send Test Email
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  function renderRegistrationTab() {
    if (domainsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const isDomainValid = (d: string) => {
      const trimmed = d.trim().toLowerCase();
      return trimmed.length > 0 && trimmed.includes('.') && !trimmed.includes('@') && !trimmed.includes(' ') && !trimmed.startsWith('http');
    };

    const handleAddDomain = () => {
      const domain = newDomain.trim().toLowerCase();
      if (!domain) return;
      if (!isDomainValid(domain)) {
        setNewDomainError('Invalid domain format (e.g. yahoo.com)');
        return;
      }
      if (blockedDomains.includes(domain)) {
        setNewDomainError('Domain already blocked');
        return;
      }
      setBlockedDomains((prev) => [...prev, domain].sort());
      setDomainsDirty(true);
      setNewDomain('');
      setNewDomainError('');
    };

    const handleRemoveDomain = (domain: string) => {
      setBlockedDomains((prev) => prev.filter((d) => d !== domain));
      setDomainsDirty(true);
    };

    const handleTestEmail = async () => {
      if (!testEmail) return;
      setTestLoading(true);
      setTestResult(null);
      try {
        const res = await adminService.validateEmailDomain(testEmail);
        setTestResult({ allowed: res.data.allowed, domain: testEmail.split('@')[1] });
      } catch {
        showNotification('Failed to validate email', 'error');
      } finally {
        setTestLoading(false);
      }
    };

    const handleBulkImport = () => {
      const entries = bulkInput
        .split(/[\n,;\s]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s && isDomainValid(s));
      const unique = entries.filter((d) => !blockedDomains.includes(d));
      if (unique.length > 0) {
        setBlockedDomains((prev) => [...prev, ...unique].sort());
        setDomainsDirty(true);
      }
      setBulkDialogOpen(false);
      setBulkInput('');
      showNotification(`${unique.length} new domain(s) added`, 'success');
    };

    const handleResetToDefaults = () => {
      setBlockedDomains([...DEFAULT_BLOCKED_DOMAINS].sort());
      setDomainsDirty(true);
      setResetDialogOpen(false);
    };

    const handleSaveDomains = async () => {
      setDomainsSaving(true);
      try {
        await adminService.updateBlockedDomains(blockedDomains);
        setOriginalDomains([...blockedDomains]);
        setDomainsDirty(false);
        showNotification('Blocked domains saved', 'success');
      } catch {
        showNotification('Failed to save blocked domains', 'error');
      } finally {
        setDomainsSaving(false);
      }
    };

    const filteredDomains = domainFilter
      ? blockedDomains.filter((d) => d.includes(domainFilter.toLowerCase()))
      : blockedDomains;

    const bulkPreviewCount = bulkInput
      .split(/[\n,;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s && isDomainValid(s) && !blockedDomains.includes(s)).length;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Blocked Email Domains
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Domains in this list are blocked from self-registration. Students with these email domains cannot create accounts.
          </Typography>
        </Box>

        {/* Add domain */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            placeholder="Add domain (e.g. yahoo.com)"
            value={newDomain}
            onChange={(e) => { setNewDomain(e.target.value); setNewDomainError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); } }}
            error={!!newDomainError}
            helperText={newDomainError}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={handleAddDomain}
            sx={{ height: 40 }}
          >
            Add
          </Button>
        </Box>

        {/* Test email */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            placeholder="Test email (e.g. user@corp.com)"
            value={testEmail}
            onChange={(e) => { setTestEmail(e.target.value); setTestResult(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleTestEmail(); } }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleTestEmail}
            disabled={testLoading || !testEmail.includes('@')}
            sx={{ height: 40 }}
          >
            {testLoading ? <CircularProgress size={18} /> : 'Test'}
          </Button>
        </Box>
        {testResult && (
          <Box>
            {testResult.allowed ? (
              <Chip label="✓ Allowed" color="success" size="small" />
            ) : (
              <Chip label={`✗ Blocked (domain: ${testResult.domain})`} color="error" size="small" />
            )}
          </Box>
        )}

        {/* Domain count + filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Total: {blockedDomains.length} blocked domain{blockedDomains.length !== 1 ? 's' : ''}
          </Typography>
          {blockedDomains.length > 20 && (
            <TextField
              size="small"
              placeholder="Filter..."
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 200 }}
            />
          )}
        </Box>

        {/* Domain list */}
        <Paper
          variant="outlined"
          sx={{ maxHeight: 300, overflow: 'auto', borderRadius: '8px' }}
        >
          <List dense disablePadding>
            {filteredDomains.map((domain) => (
              <ListItem
                key={domain}
                secondaryAction={
                  <IconButton edge="end" size="small" onClick={() => handleRemoveDomain(domain)}>
                    <Close fontSize="small" />
                  </IconButton>
                }
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <ListItemText
                  primary={domain}
                  primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
                />
              </ListItem>
            ))}
            {filteredDomains.length === 0 && (
              <ListItem>
                <ListItemText
                  primary={domainFilter ? 'No domains match filter' : 'No blocked domains'}
                  primaryTypographyProps={{ color: 'text.secondary', textAlign: 'center' }}
                />
              </ListItem>
            )}
          </List>
        </Paper>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={domainsSaving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />}
            disabled={domainsSaving || !domainsDirty}
            onClick={handleSaveDomains}
          >
            {domainsSaving ? 'Saving...' : 'Save Domains'}
          </Button>
          {domainsDirty && (
            <Chip
              icon={<WarningAmberOutlined />}
              label="Unsaved changes"
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" size="small" onClick={() => setBulkDialogOpen(true)}>
            Bulk Import
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={<RestartAlt />}
            onClick={() => setResetDialogOpen(true)}
          >
            Reset to Defaults
          </Button>
        </Box>

        {/* Bulk Import Dialog */}
        <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Bulk Import Domains</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Enter domains, one per line or comma-separated"
              value={bulkInput}
              onChange={(e) => setBulkInput(e.target.value)}
              sx={{ mt: 1 }}
            />
            {bulkInput && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {bulkPreviewCount} new domain{bulkPreviewCount !== 1 ? 's' : ''} will be added
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleBulkImport} disabled={bulkPreviewCount === 0}>
              Import
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Confirmation Dialog */}
        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
          <DialogTitle>Reset to Default Domains?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This will replace the current list with the default blocked domains ({DEFAULT_BLOCKED_DOMAINS.length} domains). Are you sure?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="warning" onClick={handleResetToDefaults}>
              Reset
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  function renderFeatureFlagsTab() {
    const featureKeys = [
      {
        key: 'features.demo_credentials',
        label: 'Demo Credentials',
        description: 'Show demo credentials on the login page for easy testing',
      },
      {
        key: 'features.help_requests',
        label: 'Help Requests',
        description: 'Allow students to submit help requests during tech eval sessions',
      },
      {
        key: 'features.real_time_updates',
        label: 'Real-Time Updates',
        description: 'Enable WebSocket-based real-time progress and status updates',
      },
      {
        key: 'features.student_self_registration',
        label: 'Student Self-Registration',
        description: 'Enable self-registration: adds Register tile to login page, email verification flow, and Forgot Password link. When disabled, login page shows only Sign In and SSO.',
      },
    ];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {featureKeys.map((feature) => (
          <Paper
            key={feature.key}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={500}>
                {feature.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.description}
              </Typography>
            </Box>
            <Switch
              checked={formValues[feature.key] === true}
              onChange={(e) => handleFieldChange(feature.key, e.target.checked)}
            />
          </Paper>
        ))}
      </Box>
    );
  }
};

export default AdminSettings;
